/**
 * Timezone-aware period boundary maths for the report period options.
 *
 * The report window edges MUST be snapped in the *report's* IANA timezone (the
 * same zone the backend buckets in), not the browser's local zone. Using
 * `date-fns` `startOfDay(...).toISOString()` snaps in the browser's local zone
 * and then serialises to UTC — so when the browser zone differs from the report
 * zone (e.g. a UTC browser reporting on Europe/London) the "start of day" lands
 * on the wrong hour. Concretely a daily report's start came out as
 * `2026-09-12T00:00:00Z`, which is `01:00+01:00` local — shifting the first
 * hourly bucket to 01:00 instead of 00:00 (see the hourly download/report bug).
 *
 * These helpers compute day/week/month boundaries against a target IANA zone
 * using only `Intl` (no new dependency), returning the exact UTC instant of the
 * zone's local midnight / end-of-day so the backend buckets align to local
 * midnight.
 */

/** Calendar wall-clock parts of an instant, as observed in a given IANA zone. */
interface ZonedParts {
  year: number
  month: number // 1-12
  day: number // 1-31
  hour: number
  minute: number
  second: number
  /** 0 = Sunday … 6 = Saturday, in the target zone. */
  weekday: number
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

/** Observe an instant's wall-clock calendar parts in the given IANA zone. */
function partsInZone(date: Date, timeZone: string): ZonedParts {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "short",
    hour12: false,
  })
  const parts = formatter.formatToParts(date)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ""
  // Intl may emit "24" for midnight in hour12:false; normalise to 0.
  const hour = Number(get("hour")) % 24
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hour,
    minute: Number(get("minute")),
    second: Number(get("second")),
    weekday: WEEKDAY_INDEX[get("weekday")] ?? 0,
  }
}

/**
 * Resolve a target-zone wall-clock time (Y/M/D H:M:S) to the UTC instant.
 *
 * Works by computing the zone's UTC offset at that instant (via a round-trip
 * through {@link partsInZone}) and correcting for it. A second pass handles the
 * rare DST-boundary case where the offset used differs from the offset at the
 * corrected instant.
 */
function zonedWallTimeToUtc(
  year: number,
  month: number, // 1-12
  day: number,
  hour: number,
  minute: number,
  second: number,
  millisecond: number,
  timeZone: string,
): Date {
  // Guess: treat the wall-clock as if it were UTC, then measure the zone's
  // offset there and shift by it.
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second, millisecond)
  const offsetMs = zoneOffsetMs(new Date(asUtc), timeZone)
  let result = new Date(asUtc - offsetMs)
  // Re-check the offset at the corrected instant; adjust once more if a DST
  // transition changed it. One correction pass is sufficient for standard zones.
  const offsetMs2 = zoneOffsetMs(result, timeZone)
  if (offsetMs2 !== offsetMs) {
    result = new Date(asUtc - offsetMs2)
  }
  return result
}

/** The target zone's UTC offset (ms) at the given instant. */
function zoneOffsetMs(date: Date, timeZone: string): number {
  const p = partsInZone(date, timeZone)
  // `partsInZone` is second-precision; compare against the instant floored to
  // whole seconds so the sub-second remainder doesn't leak into the offset.
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  const flooredInstant = Math.floor(date.getTime() / 1000) * 1000
  const rawOffset = asUtc - flooredInstant
  // Zone offsets are whole minutes; round to eliminate any residual error.
  return Math.round(rawOffset / 60000) * 60000
}

/** UTC instant of the start of the anchor's day, in the target zone. */
export function zonedStartOfDay(anchor: Date, timeZone: string): Date {
  const p = partsInZone(anchor, timeZone)
  return zonedWallTimeToUtc(p.year, p.month, p.day, 0, 0, 0, 0, timeZone)
}

/** UTC instant of the end of the anchor's day (23:59:59.999), in the target zone. */
export function zonedEndOfDay(anchor: Date, timeZone: string): Date {
  const p = partsInZone(anchor, timeZone)
  return zonedWallTimeToUtc(p.year, p.month, p.day, 23, 59, 59, 999, timeZone)
}

/**
 * UTC instant of the start of the anchor's ISO week (Monday 00:00) in the target
 * zone. `weekStartsOn` is 1 (Monday) to match the existing period options.
 */
export function zonedStartOfWeek(anchor: Date, timeZone: string, weekStartsOn = 1): Date {
  const p = partsInZone(anchor, timeZone)
  const diff = (p.weekday - weekStartsOn + 7) % 7
  const dayStart = zonedStartOfDay(anchor, timeZone)
  // Step back `diff` whole days from local midnight, re-snapping each time to
  // absorb any DST offset change across the week boundary.
  const back = new Date(dayStart.getTime() - diff * 24 * 60 * 60 * 1000)
  return zonedStartOfDay(back, timeZone)
}

/** UTC instant of the end of the anchor's ISO week (Sunday 23:59:59.999). */
export function zonedEndOfWeek(anchor: Date, timeZone: string, weekStartsOn = 1): Date {
  const weekStart = zonedStartOfWeek(anchor, timeZone, weekStartsOn)
  const sixDaysOn = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000)
  return zonedEndOfDay(sixDaysOn, timeZone)
}

/** UTC instant of the start of the anchor's month (1st, 00:00) in the target zone. */
export function zonedStartOfMonth(anchor: Date, timeZone: string): Date {
  const p = partsInZone(anchor, timeZone)
  return zonedWallTimeToUtc(p.year, p.month, 1, 0, 0, 0, 0, timeZone)
}

/** UTC instant of the end of the anchor's month (last day, 23:59:59.999). */
export function zonedEndOfMonth(anchor: Date, timeZone: string): Date {
  const p = partsInZone(anchor, timeZone)
  // Day 0 of next month = last day of this month.
  const nextMonth = p.month === 12 ? 1 : p.month + 1
  const nextYear = p.month === 12 ? p.year + 1 : p.year
  const firstOfNext = zonedWallTimeToUtc(nextYear, nextMonth, 1, 0, 0, 0, 0, timeZone)
  const lastDay = new Date(firstOfNext.getTime() - 24 * 60 * 60 * 1000)
  return zonedEndOfDay(lastDay, timeZone)
}

/** Subtract `n` whole days from an anchor, re-snapping to the zone's midnight. */
export function zonedSubDays(anchor: Date, n: number, timeZone: string): Date {
  const start = zonedStartOfDay(anchor, timeZone)
  const back = new Date(start.getTime() - n * 24 * 60 * 60 * 1000)
  return zonedStartOfDay(back, timeZone)
}

/** Subtract `n` weeks, returning the start-of-week in the zone. */
export function zonedSubWeeks(anchor: Date, n: number, timeZone: string, weekStartsOn = 1): Date {
  const start = zonedStartOfWeek(anchor, timeZone, weekStartsOn)
  const back = new Date(start.getTime() - n * 7 * 24 * 60 * 60 * 1000)
  return zonedStartOfWeek(back, timeZone, weekStartsOn)
}

/** Subtract `n` months, returning the start-of-month in the zone. */
export function zonedSubMonths(anchor: Date, n: number, timeZone: string): Date {
  const p = partsInZone(anchor, timeZone)
  const totalMonths = p.year * 12 + (p.month - 1) - n
  const year = Math.floor(totalMonths / 12)
  const month = (totalMonths % 12) + 1
  return zonedWallTimeToUtc(year, month, 1, 0, 0, 0, 0, timeZone)
}
